import { useCallback, useEffect, useRef, useState } from 'react'
import { prepacksApi } from '@/services/api'
import { imageGenerationErrorMessage } from '@/components/post-creator/imageGenerationJob'
import { isBannerImageUrl } from '../../../../../shared/pkg/prepacks/templateBuilder.js'

const PAINT_ERROR_FALLBACK = 'The picture could not be generated. Try again, or change the scene.'

/**
 * Runs one inline banner paint at a time. Callers own what a successful URL
 * means (a preview or settled pack variables); this hook owns the request's
 * cancellation, bounds and common result/error handling.
 */
export function usePaintBanner(orgSlug) {
  const requestRef = useRef(null)
  const [painting, setPainting] = useState(false)

  const cancel = useCallback(() => {
    const request = requestRef.current
    if (!request) return
    requestRef.current = null
    request.controller.abort()
    setPainting(false)
  }, [])

  useEffect(() => () => cancel(), [cancel])

  const paint = useCallback(async ({ prompt, fill, title, layout, templateId, prepackId, session, aiAllowed, sport, league, legs }, { onSuccess, onError } = {}) => {
    const scene = String(prompt || '').trim()
    if (!scene || requestRef.current) return null

    const controller = new AbortController()
    const request = { controller }
    requestRef.current = request
    setPainting(true)

    const payload = {
      prompt: scene,
      fill,
      sport: sport || '',
      league: league || '',
      legs: (Array.isArray(legs) ? legs : []).slice(0, 6).map((leg) => ({ event_label: leg.event_label || leg.eventLabel || '' })),
    }
    if (layout) payload.layout = layout
    if (title) payload.title = title
    if (templateId) payload.template_id = templateId
    if (prepackId) payload.prepack_id = prepackId
    if (session) {
      payload.session = session
      payload.ai_allowed = aiAllowed === true
    }

    let result
    try {
      const response = session
        ? await prepacksApi.generateBannerPreview(orgSlug, payload, { signal: controller.signal })
        : await prepacksApi.generateBanner(orgSlug, payload, { signal: controller.signal })
      result = response?.preview || response
    } catch (error) {
      if (requestRef.current === request && !controller.signal.aborted) {
        onError?.(imageGenerationErrorMessage(error, PAINT_ERROR_FALLBACK))
      }
      if (requestRef.current === request) {
        requestRef.current = null
        setPainting(false)
      }
      return null
    }

    try {
      const current = requestRef.current === request && !controller.signal.aborted
      if (current && isBannerImageUrl(result?.url)) onSuccess?.(result)
      return current && isBannerImageUrl(result?.url) ? result : null
    } finally {
      if (requestRef.current === request) {
        requestRef.current = null
        setPainting(false)
      }
    }
  }, [orgSlug])

  return { painting, paint, cancel }
}
